/**
 * Layer 3 - Detail. One receipt with its line items, reconciliation and
 * photo; one product with its price history across stores and its
 * alternatives; one store with its split and visits.
 *
 * Currency rules, inherited from BUILD-SPEC 8 / v2 5.3 verbatim: receipt
 * detail is native-first with the base figure alongside when they differ;
 * cross-currency comparisons are labelled, each price at its own historical
 * rate; there is no global currency toggle anywhere.
 *
 * The reconciliation triple is displayed with `ok` asserted by publish.py -
 * recomputing expected−actual here to double-check would itself violate
 * BUILD-SPEC 2, and the schema says so on the field.
 */
import { useEffect, useState } from "preact/hooks";

import * as api from "./api.js";
import type { Summary } from "./contracts/index.js";
import { categoryColor, dateName, money, monthName } from "./format.js";
import * as store from "./store.js";
import { CategorySplit, Lightbox, PriceChart, PricePoint, type ChartPoint } from "./widgets.js";

function Back({ onBack }: { onBack: () => void }) {
  return (
    <button type="button" class="back" onClick={onBack}>
      ‹ Back
    </button>
  );
}

function Missing({ what, onBack }: { what: string; onBack: () => void }) {
  return (
    <div class="detail">
      <Back onBack={onBack} />
      <p class="muted">This {what} is not in the current summary. It may have been
        republished under a different name - pull the list up again.</p>
    </div>
  );
}

// -------------------------------------------------------------- receipt ---

/**
 * The photo, when the server can still serve one. Only receipts captured
 * through the PWA have a capture to fetch from; Drive-era rows have none, and
 * a processed capture may have been pruned. Both are normal, so the photo
 * area simply is not there rather than erroring.
 */
function usePhoto(captureId: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!captureId) return;
    let revoke: string | null = null;
    let active = true;
    store
      .photoBlob(captureId, api.capturePhoto)
      .then((blob) => {
        if (!active) return;
        revoke = URL.createObjectURL(blob);
        setUrl(revoke);
      })
      .catch(() => undefined); // pruned or offline: no photo, honestly
    return () => {
      active = false;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [captureId]);
  return url;
}

export function ReceiptDetail({
  summary,
  id,
  onBack,
  onOpenProduct,
}: {
  summary: Summary;
  id: string;
  onBack: () => void;
  onOpenProduct: (name: string) => void;
}) {
  const r = summary.receipts.find((x) => x.id === id);
  const photo = usePhoto(r?.capture_id);
  const [full, setFull] = useState(false);
  if (!r) return <Missing what="receipt" onBack={onBack} />;

  const base = summary.base_currency;
  const rec = r.reconciliation;

  return (
    <div class="detail receipt">
      <Back onBack={onBack} />
      <header class="detailhead">
        <h2>{r.store}</h2>
        <p class="muted">{dateName(r.date)}</p>
        <p class="big">{money(r.total, r.currency)}</p>
        {r.currency !== base && (
          <p class="muted small">
            {money(r.total_base, base)} at rate {r.rate}
          </p>
        )}
      </header>

      {(r.discount > 0 || r.deposits != null || r.adjustments != null || r.card_charged != null) && (
        <section class="card kv">
          {r.discount > 0 && (
            <div>
              <span>Discounts</span>
              <span class="down">{money(r.discount, r.currency)}</span>
            </div>
          )}
          {r.deposits != null && (
            <div>
              <span>Deposits</span>
              <span>{money(r.deposits, r.currency)}</span>
            </div>
          )}
          {r.adjustments != null && (
            <div>
              <span>Adjustments</span>
              <span>{money(r.adjustments, r.currency)}</span>
            </div>
          )}
          {r.card_charged != null && (
            <div>
              <span>Card charged</span>
              <span>{money(r.card_charged, r.currency)}</span>
            </div>
          )}
        </section>
      )}

      {rec && (
        <section class={rec.ok ? "card recon ok" : "card recon bad"}>
          <h3>Reconciliation {rec.ok ? "✓" : "✗"}</h3>
          <div class="kv">
            <div>
              <span>Expected</span>
              <span>{money(rec.expected, r.currency)}</span>
            </div>
            <div>
              <span>Charged</span>
              <span>{money(rec.actual, r.currency)}</span>
            </div>
            <div>
              <span>Difference</span>
              <span>{money(rec.difference, r.currency)}</span>
            </div>
          </div>
          {!rec.ok && (
            <p class="small">R21: this receipt does not add up to what the card was charged.</p>
          )}
        </section>
      )}

      <section class="card">
        <h3>Categories</h3>
        <CategorySplit categories={r.categories} currency={r.currency} pct={r.categories_pct} />
      </section>

      <section class="card">
        <h3>Items</h3>
        {r.items ? (
          <table class="items">
            <tbody>
              {r.items.map((i, idx) => (
                <tr key={idx}>
                  <td>
                    <button type="button" class="linky" onClick={() => onOpenProduct(i.item)}>
                      <span class="dot" style={{ background: categoryColor(i.category) }} />
                      {i.item}
                    </button>
                    {i.deal && <span class="dealtag">{i.deal}</span>}
                  </td>
                  <td class="num muted">
                    {i.qty}× {money(i.each, r.currency)}
                  </td>
                  <td class="num">{money(i.total, r.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p class="muted small">
            {r.items_attributable
              ? "No line items recorded for this receipt."
              : "Another receipt shares this date and store, so the ledger cannot say which lines belong to which. The totals above are still exact."}
          </p>
        )}
      </section>

      {r.notes && <p class="muted small notes">{r.notes}</p>}

      {photo && (
        <section class="card">
          <h3>Photo</h3>
          <button type="button" class="photobtn" aria-label="View photo full screen" onClick={() => setFull(true)}>
            <img class="receiptphoto" src={photo} alt={`Receipt from ${r.store}, ${r.date}`} />
          </button>
          {full && <Lightbox url={photo} onClose={() => setFull(false)} />}
        </section>
      )}
      <p class="muted small">Source: {r.source}</p>
    </div>
  );
}

// -------------------------------------------------------------- product ---

/** Direction-independent, case-insensitive pair identity - mirrors the
 *  server's, so a decision always finds its pair. */
function pairKey(e: { item_a: string; store_a: string; item_b: string; store_b: string }): string {
  const side = (i: string, s: string) => `${i.trim().toLowerCase()}@@${s.trim().toLowerCase()}`;
  return [side(e.item_a, e.store_a), side(e.item_b, e.store_b)].sort().join("||");
}

export function ProductDetail({
  summary,
  name,
  onBack,
  onOpenStore,
  equivEdits = [],
  onEquivEdit,
}: {
  summary: Summary;
  name: string;
  onBack: () => void;
  onOpenStore: (name: string) => void;
  /** Decisions already recorded on the server but not yet through a run. */
  equivEdits?: api.EquivalentEdit[];
  onEquivEdit?: (edit: api.EquivalentEdit) => void | Promise<void>;
}) {
  const p = summary.products.find((x) => x.name === name);
  const [adding, setAdding] = useState(false);
  const [otherItem, setOtherItem] = useState("");
  const [otherStore, setOtherStore] = useState("");
  const [myStore, setMyStore] = useState("");
  const [relation, setRelation] = useState<"same" | "similar">("same");
  const [note, setNote] = useState("");
  if (!p) return <Missing what="product" onBack={onBack} />;

  const base = summary.base_currency;
  // Plot base figures when present so a cross-currency history is one line at
  // historical rates; otherwise the native figure is the base figure.
  const points: ChartPoint[] = p.history.map((h) => ({
    value: h.each_base ?? h.each,
    date: h.date,
    store: h.store,
  }));
  // Labels in the currency the plotted values are in: base when the history
  // spans currencies, native otherwise.
  const chartCurrency = p.cross_currency ? base : (p.history[0]?.currency ?? base);

  // Every pair involving this product, with any in-app decision overlaid.
  // The summary only changes at the next publish, so a fresh decision is
  // shown from `equivEdits` with an honest "from the next run" tag.
  const editByKey = new Map(equivEdits.map((e) => [pairKey(e), e]));
  const alts = (summary.equivalents ?? [])
    .filter((e) => e.item_a === p.name || e.item_b === p.name)
    .map((e) => ({ alt: e, edit: editByKey.get(pairKey(e)) }))
    .filter(({ alt, edit }) => (edit?.status ?? alt.status) !== "rejected");
  const altKeys = new Set(alts.map(({ alt }) => pairKey(alt)));
  // Pairs added in the app that no publish has priced yet.
  const freshAdds = equivEdits.filter(
    (e) =>
      e.status === "accepted" &&
      !altKeys.has(pairKey(e)) &&
      (e.item_a.toLowerCase() === p.name.toLowerCase() ||
        e.item_b.toLowerCase() === p.name.toLowerCase()),
  );

  return (
    <div class="detail product">
      <Back onBack={onBack} />
      <header class="detailhead">
        <h2>
          <span class="dot" style={{ background: categoryColor(p.category) }} /> {p.name}
        </h2>
        <p class="muted">
          {p.category} · {p.stores.join(", ")}
        </p>
      </header>

      {p.cross_currency && (
        <p class="banner note">
          Prices span currencies. Comparisons use each purchase's own historical rate, and no
          price alert fires across a currency boundary.
        </p>
      )}

      <section class="grid stats">
        {p.meta?.unit_price !== undefined && p.meta.unit_price_per && (
          <div>
            <h3>Unit price</h3>
            <p>
              {money(p.meta.unit_price, base)}/{p.meta.unit_price_per}
            </p>
            <p class="muted small">
              {p.meta.size_qty}
              {p.meta.size_unit} pack, confirmed
            </p>
          </div>
        )}
        <div>
          <h3>Bought</h3>
          <p>{p.times_bought}×</p>
        </div>
        <div>
          <h3>Total spent</h3>
          <p>{money(p.total_spent, base)}</p>
        </div>
        <div>
          <h3>Average each</h3>
          <p>{money(p.avg_each, base)}</p>
        </div>
        <div>
          <h3>Quantity</h3>
          <p>{p.total_qty}</p>
        </div>
      </section>

      <section class="card">
        <h3>Price history</h3>
        <PriceChart points={points} currency={chartCurrency} />
        <ul class="history">
          {[...p.history].reverse().map((h, i) => (
            <li key={i}>
              <span class="muted">{dateName(h.date)}</span>
              <span>{h.store}</span>
              <span class="num">{money(h.each, h.currency)}</span>
            </li>
          ))}
        </ul>
      </section>

      <section class="pricecards">
        {p.latest && <PricePoint title="Latest" point={p.latest} />}
        {p.previous_same_store && (
          <PricePoint title="Previous, same store" point={p.previous_same_store} />
        )}
        {p.cheapest && <PricePoint title="Cheapest seen" point={p.cheapest} />}
      </section>

      {p.by_store && p.by_store.length > 1 && (
        <section class="card">
          <h3>Latest at each store</h3>
          <ul class="history">
            {p.by_store.map((h) => (
              <li key={h.store}>
                <button type="button" class="linky" onClick={() => onOpenStore(h.store)}>
                  {h.store}
                </button>
                <span class="muted small">{dateName(h.date)}</span>
                <span class="num">{money(h.each, h.currency)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section class="card">
        <h3>Alternatives</h3>
        {alts.length || freshAdds.length ? (
          <ul class="alts">
            {alts.map(({ alt: e, edit }, i) => {
              const mine = e.item_a === p.name;
              const otherItem = mine ? e.item_b : e.item_a;
              const otherStore = mine ? e.store_b : e.store_a;
              const otherPrice = mine ? e.price_b : e.price_a;
              const effective = edit?.status ?? e.status;
              return (
                <li key={i} class="card sub">
                  <p>
                    <strong>{otherItem}</strong> at {otherStore}
                    <span class="muted small"> · {e.relation}, confidence {e.confidence}/10</span>
                  </p>
                  {otherPrice && (
                    <p class="num">
                      {money(otherPrice.each, otherPrice.currency)}
                      <span class="muted small"> on {dateName(otherPrice.date)}</span>
                    </p>
                  )}
                  {e.difference !== null && e.difference !== undefined && (
                    // difference is price_a − price_b from the file. Which side
                    // "there" is depends on which side this product is - and
                    // zero is its own case, because "€0.00 dearer" is nonsense.
                    e.difference === 0 ? (
                      <p class="muted">Same price</p>
                    ) : (
                      <p class={(mine ? e.difference > 0 : e.difference < 0) ? "down" : "up"}>
                        {money(Math.abs(e.difference), base)}{" "}
                        {(mine ? e.difference > 0 : e.difference < 0) ? "cheaper" : "dearer"} there
                      </p>
                    )
                  )}
                  {e.cross_currency && (
                    <p class="muted small">Cross-currency: compared at historical rates, never alerted on.</p>
                  )}
                  {e.note && <p class="muted small">{e.note}</p>}

                  {effective === "pending" && onEquivEdit ? (
                    <p class="altactions">
                      <span class="muted small">Suggested by the run - is it right?</span>
                      <button
                        type="button"
                        onClick={() =>
                          void onEquivEdit({
                            item_a: e.item_a,
                            store_a: e.store_a,
                            item_b: e.item_b,
                            store_b: e.store_b,
                            status: "accepted",
                            relation: e.relation,
                          })
                        }
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          void onEquivEdit({
                            item_a: e.item_a,
                            store_a: e.store_a,
                            item_b: e.item_b,
                            store_b: e.store_b,
                            status: "rejected",
                            relation: e.relation,
                          })
                        }
                      >
                        Reject
                      </button>
                    </p>
                  ) : effective === "pending" ? (
                    <p class="muted small">Suggested - not yet confirmed.</p>
                  ) : null}
                  {edit && (
                    <p class="muted small">Decision recorded - applies from the next run.</p>
                  )}
                </li>
              );
            })}
            {freshAdds.map((e, i) => {
              const mine = e.item_a.toLowerCase() === p.name.toLowerCase();
              return (
                <li key={`fresh-${i}`} class="card sub">
                  <p>
                    <strong>{mine ? e.item_b : e.item_a}</strong> at {mine ? e.store_b : e.store_a}
                    <span class="muted small"> · {e.relation ?? "same"}</span>
                  </p>
                  {e.note && <p class="muted small">{e.note}</p>}
                  <p class="muted small">Added - priced after the next run.</p>
                </li>
              );
            })}
          </ul>
        ) : (
          <p class="muted small">
            No equivalents recorded for this product yet. The run suggests them when it sees the
            same canonical name at a new store, or link one yourself below.
          </p>
        )}

        {onEquivEdit &&
          (adding ? (
            <form
              class="altform"
              onSubmit={(e) => {
                e.preventDefault();
                if (!otherItem.trim() || !otherStore.trim()) return;
                void onEquivEdit({
                  item_a: p.name,
                  store_a: myStore || (p.latest?.store ?? p.stores[0] ?? ""),
                  item_b: otherItem.trim(),
                  store_b: otherStore.trim(),
                  status: "accepted",
                  relation,
                  note: note.trim() || undefined,
                });
                setAdding(false);
                setOtherItem("");
                setOtherStore("");
                setNote("");
              }}
            >
              <label>
                This product at
                <select value={myStore || (p.latest?.store ?? p.stores[0] ?? "")}
                  onChange={(ev) => setMyStore((ev.target as HTMLSelectElement).value)}>
                  {p.stores.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label>
                Is the same as
                <input
                  list="dl-products"
                  placeholder="Product name"
                  value={otherItem}
                  onInput={(ev) => setOtherItem((ev.target as HTMLInputElement).value)}
                  required
                />
              </label>
              <label>
                At store
                <input
                  list="dl-stores"
                  placeholder="Store"
                  value={otherStore}
                  onInput={(ev) => setOtherStore((ev.target as HTMLInputElement).value)}
                  required
                />
              </label>
              <label>
                Relation
                <select value={relation}
                  onChange={(ev) => setRelation((ev.target as HTMLSelectElement).value as "same" | "similar")}>
                  <option value="same">same product</option>
                  <option value="similar">similar product</option>
                </select>
              </label>
              <label>
                Note (optional)
                <input value={note} onInput={(ev) => setNote((ev.target as HTMLInputElement).value)} />
              </label>
              <datalist id="dl-products">
                {summary.products.map((pr) => (
                  <option key={pr.name} value={pr.name} />
                ))}
              </datalist>
              <datalist id="dl-stores">
                {summary.stores.map((s) => (
                  <option key={s.name} value={s.name} />
                ))}
              </datalist>
              <div class="altformactions">
                <button type="submit" class="primary">Link products</button>
                <button type="button" onClick={() => setAdding(false)}>Cancel</button>
              </div>
            </form>
          ) : (
            <button type="button" class="link" onClick={() => setAdding(true)}>
              + Link an equivalent product at another store
            </button>
          ))}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- store ---

export function StoreDetail({
  summary,
  name,
  onBack,
  onOpenReceipt,
}: {
  summary: Summary;
  name: string;
  onBack: () => void;
  onOpenReceipt: (id: string) => void;
}) {
  const s = summary.stores.find((x) => x.name === name);
  if (!s) return <Missing what="store" onBack={onBack} />;
  const cur = summary.base_currency;
  const receipts = summary.receipts.filter((r) => r.store === s.name);

  return (
    <div class="detail store">
      <Back onBack={onBack} />
      <header class="detailhead">
        <h2>{s.name}</h2>
        <p class="big">{money(s.total, cur)}</p>
        <p class="muted small">
          {s.visits} visits · {money(s.avg_per_visit, cur)} per visit · last {dateName(s.last_visit)}
        </p>
      </header>

      <section class="card">
        <h3>Categories</h3>
        <CategorySplit categories={s.by_category} currency={cur} pct={s.by_category_pct} />
      </section>

      <section class="card">
        <h3>Receipts</h3>
        <ul class="rows tight">
          {receipts.map((r) => (
            <li key={r.id} class="row">
              <button type="button" class="rowmain" onClick={() => onOpenReceipt(r.id)}>
                <span class="title">{dateName(r.date)}</span>
                <span class="sub">{monthName(r.date.slice(0, 7))}</span>
                <span class="amount">{money(r.total, r.currency)}</span>
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
